import React from "react";
import { 
  Shield, 
  Lock, 
  Eye, 
  Bell,
  Share2,
  Database,
  Globe,
  Mail,
  Phone,
  MapPin as MapPinIcon,
  FileText,
  CheckCircle,
  AlertCircle
} from "lucide-react";

function PrivacyPolicy() {
  const sections = [
    {
      id: "overview",
      title: "1. Overview",
      icon: Eye,
      content: `This Privacy Policy explains how QUICKIE TOP – F.Z.E - Limited (the legal entity) operating under the service name "YES CHARGE" ("YES CHARGE", "we", "us", "our") collects, uses, shares, and protects your personal information when you use the YES CHARGE mobile web application, and related services (collectively, the "Services").

YES CHARGE provides B2C and B2B digital products and services, including international mobile top-up and telecom bundles, games, and social media gifts and credits. By accessing or using the Services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection and processing of your information as described herein.

This Privacy Policy applies to all users of the Services, including customers, merchants, agents, and resellers. Where required, certain features (such as uploading receipts or verification documents, selecting a beneficiary from contacts, and receiving notifications) may require device permissions, which can be managed through your device settings. YES CHARGE is operated from the United Arab Emirates (UAE) and may be used by customers worldwide.`
    },
    {
      id: "information-collect",
      title: "2. Information We Collect",
      icon: Database,
      subsections: [
        {
          title: "A) Information You Provide",
          items: [
            "Account information: name, phone number, email, username, and password (encrypted)",
            "Business/merchant information (B2B): company name, representative name, business address, registration information",
            "Support communications: messages, complaints, attachments/screenshots you send us",
            "Verification information (where applicable): basic verification details required for merchant onboarding and account protection"
          ]
        },
        {
          title: "B) Transaction & Usage Information",
          items: [
            "Transaction details: product type (top-up/bundle/game/social), amount, currency, transaction time, status, and reference/receipt IDs",
            "Pricing/commission (B2B): merchant discounts, commissions, settlements (as applicable)"
          ]
        },
        {
          title: "C) Device & Technical Information",
          items: [
            "Device model, OS version, app version",
            "IP address, language, time zone, network type, carrier",
            "Device identifiers (e.g., advertising ID) where enabled by your settings",
            "Diagnostics, crash logs, and performance logs"
          ]
        },
        {
          title: "D) App Permissions and Device Features",
          items: [
            "Photos / Gallery (Storage): to allow you to upload payment receipts, verification documents, or other supporting images when required",
            "Camera (optional): to capture and upload documents or receipts directly within the app",
            "Contacts (optional): to help you select a beneficiary mobile number more quickly for top-up/bundles",
            "Notifications: to send transaction confirmations, security alerts, service updates, and important account messages",
            "Location Information: If you enable it, we may collect approximate location to prevent fraud and improve service reliability"
          ]
        },
        {
          title: "E) Biometric Authentication (Optional)",
          content: "YES CHARGE may support biometric authentication (e.g., fingerprint/face unlock) to help you securely access the app on supported devices. Biometric data is processed by your device operating system and is not stored by YES CHARGE. You can enable or disable biometric login within your device/app settings."
        }
      ]
    },
    {
      id: "not-collect",
      title: "3. What We Do Not Collect",
      icon: AlertCircle,
      content: "YES CHARGE does not collect or access the following unless you explicitly use a feature that requires it and grant permission:",
      items: [
        "Your microphone recordings",
        "Background camera or background photo access",
        "Your SMS content",
        "Your personal files unrelated to uploads you choose",
        "Continuous location tracking (we do not track your precise location in the background)"
      ]
    },
    {
      id: "how-we-use",
      title: "4. How We Use Your Information",
      icon: Share2,
      items: [
        "Provide and deliver digital products (top-ups, bundles, game credits, social credits)",
        "Process transactions, confirmations, and receipts",
        "Provide customer support and resolve disputes",
        "Prevent fraud, abuse, and unauthorized access",
        "Monitor performance, troubleshoot errors, and improve the Services",
        "Manage B2B merchant accounts, commissions, and settlements",
        "Communicate service updates, maintenance notices, and policy changes",
        "Comply with legal obligations and respond to lawful requests"
      ]
    },
    {
      id: "legal-bases",
      title: "5. Legal Bases (General)",
      icon: FileText,
      content: "Where applicable, we process personal data based on: performance of a contract, legitimate interests (security and fraud prevention), consent (optional features/marketing), and legal obligations."
    },
    {
      id: "how-we-share",
      title: "6. How We Share Your Information",
      icon: Share2,
      subsections: [
        {
          title: "A) Service Providers (Processors)",
          content: "Hosting, analytics, customer support tools, notification services, and security providers—only as needed to operate the Services."
        },
        {
          title: "B) Third-Party Product/Service Partners",
          content: "Telecom operators/aggregators to complete top-ups and bundles; game publishers/distributors to deliver game products; social media/third-party platforms where required to deliver gifts/credits. Partners may receive transaction references and required identifiers to complete delivery."
        },
        {
          title: "C) Legal, Safety, and Compliance",
          content: "We may share information if required by law, court order, or to protect rights, safety, and prevent fraud."
        },
        {
          title: "D) Business Transfers",
          content: "If YES CHARGE is involved in a merger, acquisition, or asset sale, user information may be transferred subject to lawful safeguards."
        }
      ],
      note: "We do not sell your personal information as a standalone business."
    },
    {
      id: "data-retention",
      title: "7. Data Retention, Audit Trails, and Logs",
      icon: Database,
      content: "We retain personal data only as long as necessary for providing the Services and meeting legal, regulatory, audit, and security obligations. This may include:",
      items: [
        "Transaction records and receipts (including references, timestamps, and status)",
        "KYC submissions and verification outcomes (where applicable)",
        "Support tickets and dispute records",
        "Security logs and audit trails, including login history, access logs, device identifiers, IP addresses, and system activity logs used for fraud prevention, incident response, and compliance"
      ],
      note: "Retention periods may vary depending on legal requirements and internal risk controls. Where possible, data is anonymized or securely deleted after retention periods expire."
    },
    {
      id: "security",
      title: "8. Security",
      icon: Lock,
      content: "We apply reasonable administrative, technical, and physical safeguards such as access controls, encryption in transit (and where applicable at rest), logging and monitoring, and periodic backup and recovery checks. No method is 100% secure, but we work to protect your information."
    },
    {
      id: "rights",
      title: "9. Your Rights and Choices",
      icon: CheckCircle,
      content: "Depending on your location and applicable laws, you may request access, correction, deletion (where permitted), withdrawal of consent, or objection to certain processing. To request this, contact: support@yes-charge.com."
    },
    {
      id: "marketing",
      title: "10. Marketing and Notifications",
      icon: Bell,
      content: "You may receive transactional messages (e.g., receipts, Offers, status updates). Marketing messages are optional and can be disabled via app settings or by contacting us."
    },
    {
      id: "children",
      title: "11. Children's Privacy",
      icon: Eye,
      content: "YES CHARGE Services are not intended for children under [13]. If you believe a child provided data, contact us to remove it."
    },
    {
      id: "international",
      title: "12. International Transfers",
      icon: Globe,
      content: "If we use hosting or service providers outside the UAE, your data may be processed in those locations with appropriate safeguards."
    },
    {
      id: "changes",
      title: "13. Changes to This Policy",
      icon: FileText,
      content: "We may update this Privacy Policy. We will post the updated version in the app and update the 'Last Updated' date."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="bg-gradient-to-r from-secondary-600 to-primary-600 dark:from-secondary-700 dark:to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex p-4 bg-white/20 rounded-full mb-4 backdrop-blur-sm">
            <Shield size={32} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Privacy Policy</h1>
          <div className="flex justify-center gap-4 mt-4">
            <p className="text-white/90 text-lg">
              Effective Date: 6-Mar-2026 | Last Updated: 6-Mar-2026
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} id={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-lg overflow-hidden transition-colors duration-200">
                <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg">
                      <Icon className="text-secondary-600 dark:text-secondary-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{section.title}</h2>
                  </div>
                </div>
                
                <div className="px-6 py-5">
                  {section.content && (
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 whitespace-pre-line">
                      {section.content}
                    </p>
                  )}
                  
                  {section.items && (
                    <ul className="space-y-2 mb-4">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                          <div className="flex-shrink-0 w-1.5 h-1.5 bg-secondary-600 dark:bg-secondary-400 rounded-full mt-2"></div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.subsections && section.subsections.map((subsection, idx) => (
                    <div key={idx} className="mb-4 last:mb-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{subsection.title}</h3>
                      {subsection.content && (
                        <p className="text-gray-700 dark:text-gray-300 mb-2">{subsection.content}</p>
                      )}
                      {subsection.items && (
                        <ul className="space-y-1 ml-4">
                          {subsection.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                              <div className="flex-shrink-0 w-1.5 h-1.5 bg-secondary-400 dark:bg-secondary-500 rounded-full mt-2"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  
                  {section.note && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2 border-l-2 border-secondary-300 dark:border-secondary-600 pl-3">
                      {section.note}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20 rounded-xl p-8 border border-secondary-100 dark:border-secondary-800 transition-colors duration-200">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Mail className="text-secondary-600 dark:text-secondary-400" size={24} />
              14. Contact Us
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">QUICKIE TOP – F.Z.E - Limited</h3>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <div className="flex items-start gap-2">
                    <MapPinIcon size={18} className="text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-0.5" />
                    <span>B.C. 1301499, Ajman Free Zone C1 Building, Ajman, United Arab Emirates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={18} className="text-secondary-600 dark:text-secondary-400" />
                    <a href="mailto:support@Yes-Charge.com" className="text-secondary-600 dark:text-secondary-400 hover:underline">
                      support@Yes-Charge.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={18} className="text-secondary-600 dark:text-secondary-400" />
                    <span>+1 (279) 790-1187</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 transition-colors duration-200">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For any questions about this Privacy Policy or our data handling practices, 
                  please don't hesitate to contact us.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;